import { ActionIcon, Anchor, Divider, Group, Indicator, Popover, ScrollArea, Stack, Text, Tooltip, UnstyledButton } from '@mantine/core';
import { IconBell } from '@tabler/icons-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useMarkAllNotificationsRead, useMarkNotificationRead, useNotifications, type AppNotification } from '@/api/notifications';
import dayjs from '@/lib/dayjs';

/** Glocke mit In-App-Benachrichtigungen (NOT-01). */
export function NotificationBell() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [opened, setOpened] = useState(false);
  const { data } = useNotifications();
  const markRead = useMarkNotificationRead();
  const markAll = useMarkAllNotificationsRead();
  const unread = data?.unread ?? 0;

  const open = (n: AppNotification) => {
    if (!n.readAt) markRead.mutate(n.id);
    if (n.link) {
      setOpened(false);
      if (n.link.startsWith('http')) window.location.assign(n.link);
      else navigate(n.link);
    }
  };

  return (
    <Popover opened={opened} onChange={setOpened} position="bottom-end" width={360} shadow="md" withinPortal>
      <Popover.Target>
        <Tooltip label={t('header.notifications')} disabled={opened}>
          <Indicator disabled={unread === 0} label={unread > 9 ? '9+' : unread} size={16} offset={6}>
            <ActionIcon variant="subtle" color="gray" size="lg" aria-label={t('header.notifications')} onClick={() => setOpened((o) => !o)}>
              <IconBell size={20} />
            </ActionIcon>
          </Indicator>
        </Tooltip>
      </Popover.Target>
      <Popover.Dropdown p={0}>
        <Group justify="space-between" p="sm">
          <Text fw={600}>{t('header.notifications')}</Text>
          {unread > 0 && (
            <Anchor size="xs" component="button" onClick={() => markAll.mutate()}>
              {t('notifications.markAllRead')}
            </Anchor>
          )}
        </Group>
        <Divider />
        <ScrollArea.Autosize mah={420}>
          {(data?.items ?? []).length === 0 ? (
            <Text c="dimmed" size="sm" ta="center" p="lg">
              {t('notifications.empty')}
            </Text>
          ) : (
            <Stack gap={0}>
              {data!.items.map((n) => (
                <UnstyledButton
                  key={n.id}
                  onClick={() => open(n)}
                  p="sm"
                  style={{
                    borderBottom: '1px solid var(--mantine-color-default-border)',
                    background: n.readAt ? undefined : 'var(--mantine-primary-color-light)',
                  }}
                >
                  <Text size="sm" fw={n.readAt ? 400 : 600}>
                    {n.title}
                  </Text>
                  {n.body && (
                    <Text size="xs" c="dimmed" lineClamp={2}>
                      {n.body}
                    </Text>
                  )}
                  <Text size="xs" c="dimmed" mt={2}>
                    {dayjs(n.createdAt).fromNow()}
                  </Text>
                </UnstyledButton>
              ))}
            </Stack>
          )}
        </ScrollArea.Autosize>
      </Popover.Dropdown>
    </Popover>
  );
}
